<script setup lang="ts">
import { normalizeJatsWhitespace } from '#shared/utils/jats-heading'
import { faXmark } from '@fortawesome/pro-light-svg-icons'

const props = defineProps<{
  title: string
  sectionId: string
}>()

const { $parseStringToDocument } = useNuxtApp()

const titleHtml = computed(() => {
  return $parseStringToDocument(props.title).body.innerHTML
})

const titleText = computed(() => {
  const doc = $parseStringToDocument(titleHtml.value)
  return normalizeJatsWhitespace(doc.body.textContent ?? '')
})

interface FootnoteData {
  titleText: string
  footnoteNumber: string
  footnoteContent: string
  footnoteId: string
}

const footnoteData = computed<FootnoteData | null>(() => {
  const match = titleText.value.match(/^(.*[a-zæøå])(\d+) (.+)$/i)

  if (!match) {
    return null
  }

  return {
    titleText: match[1] ?? '',
    footnoteNumber: match[2] ?? '',
    footnoteContent: match[3] ?? '',
    footnoteId: `title-fn-${props.sectionId}-${match[2]}`,
  }
})
</script>

<template>
  <h2 v-if="footnoteData" class="title-s my-m" data-marking-content>
    {{ footnoteData.titleText }}
    <sup :data-footnote-id="footnoteData.footnoteId">
      <button
        class="footnote__btn"
        :data-footnote-id="footnoteData.footnoteId"
        aria-expanded="false"
        :aria-controls="footnoteData.footnoteId"
      >
        {{ footnoteData.footnoteNumber }}
      </button>
    </sup>
    <span
      :id="footnoteData.footnoteId"
      class="footnote__content"
      :data-footnote-id="footnoteData.footnoteId"
      hidden
    >
      <span class="flex justify-between items-center">
        <span class="footnote__label">Fotnote {{ footnoteData.footnoteNumber }}</span>
        <button
          :data-footnote-id="footnoteData.footnoteId"
          class="footnote__close-btn"
          aria-label="Lukk fotnote"
        >
          <Icon :icon="faXmark" />
        </button>
      </span>
      <span class="footnote__text" style="word-break: break-word;">
        {{ footnoteData.footnoteContent }}
      </span>
    </span>
  </h2>
  <h2 v-else class="title-s my-m" data-marking-content>
    <span v-html="titleHtml" />
  </h2>
</template>

<style scoped lang="postcss">
@keyframes footnoteFadeSlide {
  0% {
    opacity: 0;
    transform: translateY(4px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

:deep(.footnote__content:not([hidden])) {
  @apply flex flex-col gap-2xs max-w-full box-border my-s rounded-lg bg-blue-50 dark:bg-carbon-500 text-sm break-all leading-6 p-s;
  animation: footnoteFadeSlide 0.22s ease-out;

  a {
    @apply text-sm;
  }
}

:deep(.footnote__content[hidden]) {
  @apply !hidden;
}

:deep(.footnote__label) {
  @apply font-semibold text-primary mr-3xs;
}

:deep(.footnote__text) {
  @apply break-words font-normal;
  word-break: break-word;
}

:deep(.footnote__btn) {
  @apply cursor-pointer underline text-primary;
}

:deep(.footnote__close-btn) {
  @apply rounded-lg border border-blurple-500 dark:border-carbon-300 hover:bg-blurple-100 hover:border-blurple-100 dark:hover:bg-carbon-400 dark:hover:border-carbon-400 p-2xs h-fit flex items-center justify-center transition-colors;

  svg {
    @apply h-4 w-4;
  }
}
</style>
